import { Download, FileQuestion } from 'lucide-react';
import { useCallback } from 'react';

import { Button } from '../../../../components/Button';
import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { useBlobPreview } from '../../../../hooks/useBlobPreview';
import { formatBytes } from '../../../../lib/formatBytes';
import { formatDateTime } from '../../../../lib/formatDate';
import { attritionApi } from '../attritionApi';
import type { AttritionRequestDocument } from '../attritionTypes';

/**
 * One submitted document, shown at the size the screen allows, beside what is known about it —
 * the 201 file's `<DocumentPreviewModal>`, for a request's line.
 *
 * **Everything security-bearing is `useBlobPreview`'s** — the VERIFIED type off the stored file,
 * the inert allow-list and the re-typed blob. The bytes come from the same endpoint Download
 * uses, behind `attrition-requests.read` and the employee's clearance. **It writes nothing, so it
 * carries no confirm tick**, and mounting is what fetches.
 */
export function AttritionDocumentPreviewModal({
  requestId,
  document,
  download = attritionApi.downloadDocument,
  onDownload,
  onClose,
}: {
  requestId: string;
  document: AttritionRequestDocument;
  /** The surface's own endpoint — HR's by default, Self Service's for the employee. */
  download?: typeof attritionApi.downloadDocument;
  onDownload: () => void;
  onClose: () => void;
}) {
  const load = useCallback(
    (signal: AbortSignal) => download(requestId, document.id, signal),
    [download, requestId, document.id],
  );

  const { kind, url, loading, error } = useBlobPreview(document.id, document.contentType ?? null, load);
  const fileName = document.fileName ?? document.name;

  return (
    <Modal
      open
      onClose={onClose}
      title={fileName}
      size="3xl"
      id="attrition-document-preview-modal"
      footer={
        <Button
          id="attrition-document-preview-download"
          variant="secondary"
          leading={<Download className="h-4 w-4" aria-hidden="true" />}
          onClick={onDownload}
          /* The only way to save the file once its name opens this, so it stays in the tab run. */
          tabIndex={0}
        >
          Download
        </Button>
      }
    >
      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="min-w-0 flex-1">
          {kind === 'none' ? (
            <div className="flex min-h-[320px] flex-col items-center justify-center gap-4 rounded-lg bg-slate-50">
              <FileQuestion className="h-10 w-10 text-slate-300" aria-hidden="true" />
              <EmptyState minHeight="min-h-0" message="This file cannot be shown here. Download it to open it." />
            </div>
          ) : loading ? (
            <Skeleton className="h-[55vh] w-full" />
          ) : error || !url ? (
            <div className="flex min-h-[320px] items-center justify-center rounded-lg bg-slate-50">
              <ErrorState message={error?.message ?? 'Could not load the preview.'} />
            </div>
          ) : kind === 'image' ? (
            <div className="flex items-center justify-center rounded-lg bg-slate-100 p-3">
              <img src={url} alt={fileName} className="max-h-[55vh] w-auto max-w-full rounded object-contain" />
            </div>
          ) : (
            <iframe src={url} title={`Preview of ${fileName}`} className="h-[55vh] w-full rounded-lg border-0 bg-slate-50" />
          )}
        </div>

        <div className="w-full shrink-0 lg:w-80">
          <PreviewTable
            rows={[
              /* No File name row — the modal's TITLE is the file name, and a value is printed once. */
              { label: 'Document', value: document.name },
              { label: 'Status', value: document.reviewStatusLabel },
              { label: 'Type', value: document.contentType ?? '—', mono: document.contentType != null },
              {
                label: 'Size',
                value: document.sizeBytes != null ? formatBytes(document.sizeBytes) : '—',
                mono: document.sizeBytes != null,
              },
              {
                label: 'Submitted at',
                value: document.submittedAt != null ? formatDateTime(document.submittedAt) : '—',
                mono: document.submittedAt != null,
              },
              { label: 'Submitted by', value: document.submittedByName ?? '—' },
              { label: 'Reviewer', value: document.reviewerName ?? '—' },
            ]}
          />
        </div>
      </div>
    </Modal>
  );
}
